export const DEV_DOCS_PROPERTIES_KEY = 'devdocsProperties';
const SMART_FOLDER_PROPERTIES_KEY = 'smartFolderProperties';

export type DevDocsContentPropertyNode = {
	readonly key?: string | null;
	readonly value?: unknown;
} | null;

export type DevDocsProperties = {
	readonly ref: string | null;
	readonly repoName: string | null;
	readonly syncTaskAri: string | null;
};

export type SmartFolderProperties = {
	readonly containerApp: string | null;
	readonly lastSynced: string | null;
	readonly lastSyncTaskAri: string | null;
	readonly syncErrorType: string | null;
	readonly syncInProgress: boolean;
};

const parseContentPropertyValue = (propertyValue: unknown): Record<string, unknown> | null => {
	let parsedValue = propertyValue;

	try {
		for (let unwrapCount = 0; unwrapCount < 3; unwrapCount += 1) {
			if (typeof parsedValue === 'string') {
				parsedValue = JSON.parse(parsedValue) as unknown;
				continue;
			}

			if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
				return null;
			}

			const value = (parsedValue as Record<string, unknown>).value;
			if (typeof value === 'string' || (value && typeof value === 'object')) {
				parsedValue = value;
				continue;
			}

			return parsedValue as Record<string, unknown>;
		}
	} catch {
		return null;
	}

	return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
		? (parsedValue as Record<string, unknown>)
		: null;
};

const getStringValue = (parsedValue: Record<string, unknown>, key: string): string | null => {
	const value = parsedValue[key];
	return typeof value === 'string' && value.length > 0 ? value : null;
};

const getBooleanValue = (parsedValue: Record<string, unknown>, key: string): boolean =>
	parsedValue[key] === true;

const getObjectValue = (
	parsedValue: Record<string, unknown>,
	key: string,
): Record<string, unknown> | null => {
	const value = parsedValue[key];
	return value && typeof value === 'object' && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
};

export const getDevDocsProperties = (
	properties?: readonly DevDocsContentPropertyNode[] | null,
): DevDocsProperties | null => {
	const devDocsProperties = properties?.find(
		(property) => property?.key === DEV_DOCS_PROPERTIES_KEY,
	);
	const parsedValue =
		devDocsProperties?.value !== undefined
			? parseContentPropertyValue(devDocsProperties.value)
			: null;

	return parsedValue
		? {
				ref: getStringValue(getObjectValue(parsedValue, 'devDocsConfig') ?? {}, 'ref'),
				repoName: getStringValue(getObjectValue(parsedValue, 'devDocsConfig') ?? {}, 'repoName'),
				syncTaskAri: getStringValue(parsedValue, 'syncTaskAri'),
			}
		: null;
};

export const hasDevDocsContentProperty = (
	properties?: readonly DevDocsContentPropertyNode[] | null,
): boolean => Boolean(properties?.some((property) => property?.key === DEV_DOCS_PROPERTIES_KEY));

export const getDevDocsSmartFolderProperties = (
	properties?: readonly DevDocsContentPropertyNode[] | null,
): SmartFolderProperties | null => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);
	const parsedValue =
		smartFolderProperties?.value !== undefined
			? parseContentPropertyValue(smartFolderProperties.value)
			: null;

	return parsedValue
		? {
				containerApp: getStringValue(parsedValue, 'containerApp'),
				lastSynced: getStringValue(parsedValue, 'lastSynced'),
				lastSyncTaskAri: getStringValue(parsedValue, 'lastSyncTaskAri'),
				syncErrorType: getStringValue(parsedValue, 'syncErrorType'),
				syncInProgress: getBooleanValue(parsedValue, 'syncInProgress'),
			}
		: null;
};
